import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Session } from "@supabase/supabase-js";
import { useStore } from "./lib/store";
import {
  countdownLabel,
  dateAt,
  daysFromToday,
  formatDate,
  formatTime,
  monthGrid,
  nextRecurringDate,
  toIsoDate,
  todayIso,
} from "./lib/date";
import {
  playTabletopBell,
  registerServiceWorker,
  requestNotificationPermission,
  showSystemNotification,
  subscribeToPush,
  testRemotePush,
} from "./lib/notifications";
import type {
  ActivityItem,
  CalendarEvent,
  CaptureType,
  ImportantDate,
  KivItem,
  Matter,
  MobilePreferences,
  NavPage,
  NotificationItem,
  ReminderRule,
  Task,
} from "./types";
import { Icon } from "./components/Icons";
import {
  EmptyState,
  GlassCard,
  PriorityBadge,
  Segmented,
  Sheet,
  Toggle,
} from "./components/Ui";
import {
  GlassNotificationHost,
  type GlassNotice,
} from "./components/GlassNotification";
import { QuickCapture } from "./components/QuickCapture";
import "./styles.css";

const ACTIVE_MATTER_STATUSES = new Set([
  "active",
  "open",
  "ongoing",
  "in progress",
  "",
]);

function recordDate(value?: string): number {
  if (!value) return 0;
  const date = new Date(value.includes("T") ? value : `${value}T12:00:00`);
  return Number.isNaN(date.getTime()) ? 0 : date.getTime();
}

function matterName(matters: Matter[], id?: string): string {
  return matters.find((matter) => matter.id === id)?.name || "";
}

function sourceTypeForEntity(entityType: string): string {
  if (entityType === "calendar_event") return "entry";
  if (entityType === "important_date") return "important";
  return entityType;
}

function priorityWeight(priority?: string): number {
  return (
    (
      { urgent: 5, high: 4, medium: 3, normal: 2, low: 1 } as Record<
        string,
        number
      >
    )[String(priority || "normal").toLowerCase()] || 2
  );
}

function uuid(): string {
  return crypto.randomUUID();
}

function Login({ configured }: { configured: boolean }) {
  const { signIn } = useStore();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      await signIn(email.trim(), password);
    } catch (loginError) {
      setError(
        loginError instanceof Error ? loginError.message : String(loginError),
      );
    } finally {
      setBusy(false);
    }
  }
  return (
    <main className="login-shell">
      <div className="login-halo" />
      <section className="login-card md-glass">
        <img
          src="/icons/display-logo-light.png"
          className="login-logo"
          alt="Matter Dashboard"
        />
        <div className="eyebrow">MATTER DASHBOARD MOBILE</div>
        <h1>Matter Dashboard</h1>
        <p>
          Tasks, calendar, KIV and matter activity—synchronised with your
          Windows dashboard.
        </p>
        {!configured ? (
          <div className="setup-warning">
            <strong>Supabase is not configured.</strong>
            <span>
              Copy <code>.env.example</code> to <code>.env</code> and enter your
              project URL and publishable key.
            </span>
          </div>
        ) : (
          <form onSubmit={submit} className="login-form">
            <label>
              <span>Email</span>
              <input
                type="email"
                autoComplete="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                required
              />
            </label>
            <label>
              <span>Password</span>
              <input
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required
              />
            </label>
            {error && <div className="form-error">{error}</div>}
            <button className="primary-button full" disabled={busy}>
              {busy ? "Signing in…" : "Sign in"}
            </button>
          </form>
        )}
      </section>
    </main>
  );
}

interface EditTarget {
  type: CaptureType;
  entity: Task | CalendarEvent | ImportantDate | KivItem;
}

function App() {
  const store = useStore();
  const { data, session, loading } = store;
  const [page, setPage] = useState<NavPage>(
    () =>
      (new URLSearchParams(location.search).get("page") as NavPage) ||
      "dashboard",
  );
  const [captureOpen, setCaptureOpen] = useState(false);
  const [captureType, setCaptureType] = useState<CaptureType>("task");
  const [editTarget, setEditTarget] = useState<EditTarget | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [selectedMatter, setSelectedMatter] = useState<string | null>(null);
  const [notices, setNotices] = useState<GlassNotice[]>([]);
  const [pullDistance, setPullDistance] = useState(0);
  const pullStart = useRef<number | null>(null);

  const addGlassNotice = useCallback(
    (notice: Omit<GlassNotice, "id"> & { id?: string }) => {
      setNotices((current) => [
        ...current.filter((item) => item.id !== notice.id),
        { ...notice, id: notice.id || uuid() },
      ]);
    },
    [],
  );

  useEffect(() => {
    void registerServiceWorker();
  }, []);

  useEffect(() => {
    const prefs = data.preferences;
    const root = document.documentElement;
    root.dataset.theme = prefs.theme || "system";
    root.classList.toggle("reduce-motion", Boolean(prefs.reducedMotion));
    root.classList.toggle(
      "reduce-transparency",
      Boolean(prefs.reducedTransparency),
    );
  }, [data.preferences]);

  const openCapture = useCallback(
    (type: CaptureType, entity?: EditTarget["entity"]) => {
      setCaptureType(type);
      setEditTarget(entity ? { type, entity } : null);
      setCaptureOpen(true);
    },
    [],
  );

  const completeSource = useCallback(
    async (entityType: string, entityId: string) => {
      if (entityType === "task") {
        const task = data.tasks.find((item) => item.id === entityId);
        if (!task) return;
        await store.saveEntity(
          "task",
          {
            ...task,
            completed: true,
            completedAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          },
          {
            activityTitle: "Task completed on iPhone",
            activityDetail: task.title,
          },
        );
      } else if (entityType === "kiv") {
        const item = data.kivItems.find((row) => row.id === entityId);
        if (!item) return;
        const recurring = item.recurrence && item.recurrence !== "none";
        await store.saveEntity(
          "kiv",
          {
            ...item,
            lastReviewed: new Date().toISOString(),
            reviewDate: recurring
              ? nextRecurringDate(item.reviewDate, item.recurrence)
              : item.reviewDate,
            status: recurring ? "Open" : "Closed",
            updatedAt: new Date().toISOString(),
          },
          {
            activityTitle: recurring
              ? "KIV reviewed on iPhone"
              : "KIV closed on iPhone",
            activityDetail: item.title,
          },
        );
      } else if (entityType === "important_date") {
        const item = data.importantDates.find((row) => row.id === entityId);
        if (!item) return;
        await store.saveEntity(
          "important_date",
          {
            ...item,
            completed: true,
            completedAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          },
          {
            activityTitle: "Important date completed on iPhone",
            activityDetail: item.title,
          },
        );
      }
    },
    [data.importantDates, data.kivItems, data.tasks, store],
  );

  const snoozeSource = useCallback(
    async (entityType: string, entityId: string, minutes = 60) => {
      const sourceType = sourceTypeForEntity(entityType);
      const existing = data.reminderRules.find(
        (rule) => rule.sourceType === sourceType && rule.sourceId === entityId,
      );
      if (!existing) return;
      const {
        id: _id,
        sourceType: _sourceType,
        sourceId: _sourceId,
        _sourceKey: _key,
        updatedAt: _updatedAt,
        ...draft
      } = existing;
      await store.saveReminderRule(sourceType, entityId, {
        ...draft,
        snoozedUntil: new Date(Date.now() + minutes * 60000).toISOString(),
      });
      addGlassNotice({
        title: "Reminder snoozed",
        body: `Snoozed for ${minutes >= 60 ? `${minutes / 60} hour${minutes === 60 ? "" : "s"}` : `${minutes} minutes`}.`,
      });
    },
    [addGlassNotice, data.reminderRules, store],
  );

  const openEntity = useCallback(
    (entityType?: string, entityId?: string) => {
      if (!entityType || !entityId) return;
      if (entityType === "task") {
        setPage("dashboard");
        const entity = data.tasks.find((item) => item.id === entityId);
        if (entity) openCapture("task", entity);
      } else if (entityType === "calendar_event" || entityType === "entry") {
        setPage("calendar");
        const entity = data.events.find((item) => item.id === entityId);
        if (entity) openCapture("event", entity);
      } else if (
        entityType === "important_date" ||
        entityType === "important"
      ) {
        setPage("calendar");
        const entity = data.importantDates.find((item) => item.id === entityId);
        if (entity) openCapture("date", entity);
      } else if (entityType === "kiv") {
        setPage("kiv");
        const entity = data.kivItems.find((item) => item.id === entityId);
        if (entity) openCapture("kiv", entity);
      } else if (entityType === "matter") {
        setPage("matters");
        setSelectedMatter(entityId);
      }
    },
    [data.events, data.importantDates, data.kivItems, data.tasks, openCapture],
  );

  useEffect(() => {
    if (!session) return;
    const params = new URLSearchParams(location.search);
    const action = params.get("notificationAction");
    const entityType = params.get("entityType") || "";
    const entityId = params.get("entityId") || "";
    if (!entityId) return;
    if (action === "complete") void completeSource(entityType, entityId);
    else if (action === "snooze") void snoozeSource(entityType, entityId);
    else openEntity(entityType, entityId);
    params.delete("notificationAction");
    params.delete("entityType");
    params.delete("entityId");
    history.replaceState(
      {},
      "",
      `${location.pathname}${params.size ? `?${params}` : ""}`,
    );
  }, [completeSource, openEntity, session, snoozeSource]);

  useEffect(() => {
    if (session) void store.syncNow();
  }, [page, session, store.syncNow]);

  useReminderEngine({
    session,
    addGlassNotice,
    completeSource,
    snoozeSource,
    openEntity,
  });

  if (loading)
    return (
      <main className="loading-screen">
        <img src="/icons/display-logo-light.png" alt="" />
        <span>Loading Matter Dashboard…</span>
      </main>
    );
  if (!session) return <Login configured={store.configured} />;

  const navItems: Array<{ page: NavPage; label: string; icon: string }> = [
    { page: "dashboard", label: "Today", icon: "dashboard" },
    { page: "matters", label: "Matters", icon: "matters" },
    { page: "calendar", label: "Calendar", icon: "calendar" },
    { page: "kiv", label: "KIV", icon: "kiv" },
    { page: "activity", label: "Activity", icon: "activity" },
  ];

  const syncLabel = store.syncing
    ? "Syncing"
    : !store.online
      ? "Offline"
      : store.pendingCount
        ? `${store.pendingCount} queued`
        : store.realtimeStatus === "live"
          ? "Live"
          : "Synced";
  const beginPull = (event: React.TouchEvent<HTMLElement>) => {
    if (window.scrollY <= 2)
      pullStart.current = event.touches[0]?.clientY ?? null;
  };
  const movePull = (event: React.TouchEvent<HTMLElement>) => {
    if (pullStart.current === null) return;
    const distance = Math.max(
      0,
      (event.touches[0]?.clientY || 0) - pullStart.current,
    );
    setPullDistance(Math.min(76, distance * 0.55));
  };
  const finishPull = () => {
    const shouldRefresh = pullDistance >= 48;
    pullStart.current = null;
    setPullDistance(0);
    if (shouldRefresh) void store.syncNow();
  };

  return (
    <div className="mobile-app">
      <header className="app-header md-glass">
        <div className="brand-lockup">
          <img src="/icons/display-logo-light.png" alt="" />
          <div>
            <span>MATTER DASHBOARD</span>
            <strong>
              {navItems.find((item) => item.page === page)?.label ||
                "Matter Dashboard"}
            </strong>
          </div>
        </div>
        <div className="header-actions">
          <button
            className={`sync-chip ${store.online ? store.realtimeStatus : "offline"}`}
            onClick={() => void store.syncNow()}
            aria-label="Synchronise"
          >
            <Icon name={store.online ? "sync" : "offline"} />
            <span>{syncLabel}</span>
          </button>
          <button
            className="icon-button settings-button"
            onClick={() => setSettingsOpen(true)}
            aria-label="Settings"
          >
            <Icon name="settings" />
          </button>
        </div>
      </header>

      <div
        className={`pull-refresh ${pullDistance >= 48 ? "ready" : ""}`}
        style={{
          transform: `translateY(${pullDistance - 40}px)`,
          opacity: Math.min(1, pullDistance / 40),
        }}
      >
        <Icon name="sync" />
        <span>{pullDistance >= 48 ? "Release to sync" : "Pull to sync"}</span>
      </div>
      <main
        className="page-host"
        key={page}
        onTouchStart={beginPull}
        onTouchMove={movePull}
        onTouchEnd={finishPull}
        onTouchCancel={finishPull}
      >
        {page === "dashboard" && (
          <DashboardPage
            openCapture={openCapture}
            openEntity={openEntity}
            addNotice={addGlassNotice}
          />
        )}
        {page === "matters" && (
          <MattersPage
            selectedMatter={selectedMatter}
            setSelectedMatter={setSelectedMatter}
            openCapture={openCapture}
          />
        )}
        {page === "calendar" && <CalendarPage openCapture={openCapture} />}
        {page === "kiv" && (
          <KivPage openCapture={openCapture} addNotice={addGlassNotice} />
        )}
        {page === "activity" && (
          <ActivityPage
            openEntity={openEntity}
            completeSource={completeSource}
            snoozeSource={snoozeSource}
          />
        )}
      </main>

      <button
        className="quick-capture-fab"
        onClick={() => openCapture("task")}
        aria-label="Quick Capture"
      >
        <Icon name="plus" />
      </button>
      <nav className="bottom-nav md-glass">
        {navItems.map((item) => (
          <button
            key={item.page}
            className={page === item.page ? "active" : ""}
            onClick={() => setPage(item.page)}
          >
            <span className="nav-icon">
              <Icon name={item.icon} />
            </span>
            <span>{item.label}</span>
          </button>
        ))}
      </nav>

      <QuickCapture
        open={captureOpen}
        initialType={captureType}
        editEntity={editTarget?.entity || null}
        editType={editTarget?.type}
        onClose={() => {
          setCaptureOpen(false);
          setEditTarget(null);
        }}
      />
      <SettingsSheet
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        addNotice={addGlassNotice}
      />
      <GlassNotificationHost
        notices={notices}
        dismiss={(id) =>
          setNotices((current) => current.filter((item) => item.id !== id))
        }
      />
    </div>
  );
}

function DashboardPage({
  openCapture,
  openEntity,
  addNotice,
}: {
  openCapture: (
    type: CaptureType,
    entity?: Task | CalendarEvent | ImportantDate | KivItem,
  ) => void;
  openEntity: (type?: string, id?: string) => void;
  addNotice: (notice: Omit<GlassNotice, "id"> & { id?: string }) => void;
}) {
  const { data, saveEntity } = useStore();
  const [view, setView] = useState<"today" | "tasks">("today");
  const [taskFilter, setTaskFilter] = useState<
    "open" | "today" | "overdue" | "completed"
  >("open");
  const [taskSearch, setTaskSearch] = useState("");
  const activeTasks = data.tasks.filter((task) => !task.completed);
  const overdue = activeTasks
    .filter((task) => (daysFromToday(task.dueDate) ?? 1) < 0)
    .sort((a, b) => recordDate(a.dueDate) - recordDate(b.dueDate));
  const todayTasks = activeTasks
    .filter((task) => task.dueDate === todayIso())
    .sort((a, b) => priorityWeight(b.priority) - priorityWeight(a.priority));
  const dueKiv = data.kivItems.filter(
    (item) =>
      String(item.status || "").toLowerCase() !== "closed" &&
      (daysFromToday(item.reviewDate) ?? 1) <= 0,
  );
  const upcoming = useMemo(() => {
    const rows = [
      ...activeTasks.map((item) => ({
        kind: "Task",
        id: item.id,
        title: item.title,
        date: item.dueDate || "",
        time: item.reminderTime || item.googleCalendarTime || "",
        matterId: item.matterId,
        priority: item.priority,
        type: "task",
      })),
      ...data.events.map((item) => ({
        kind: "Calendar",
        id: item.id,
        title: item.title,
        date: item.date,
        time: item.time || "",
        matterId: item.matterId,
        priority: "Normal",
        type: "calendar_event",
      })),
      ...data.importantDates
        .filter((item) => !item.completed)
        .map((item) => ({
          kind: item.dateType || item.type || "Important Date",
          id: item.id,
          title: item.title,
          date: item.date,
          time: item.time || "",
          matterId: item.matterId,
          priority: item.priority,
          type: "important_date",
        })),
      ...data.kivItems
        .filter((item) => String(item.status || "").toLowerCase() !== "closed")
        .map((item) => ({
          kind: "KIV",
          id: item.id,
          title: item.title,
          date: item.reviewDate,
          time: item.reviewTime || "",
          matterId: item.matterId,
          priority: item.priority,
          type: "kiv",
        })),
    ];
    return rows
      .filter((item) => (daysFromToday(item.date) ?? -1) >= 0)
      .sort(
        (a, b) =>
          recordDate(a.date) - recordDate(b.date) ||
          priorityWeight(b.priority) - priorityWeight(a.priority),
      )
      .slice(0, 8);
  }, [activeTasks, data.events, data.importantDates, data.kivItems]);
  const notifications = data.notifications
    .filter((item) => !item.archived && !item.read)
    .sort((a, b) => recordDate(b.createdAt) - recordDate(a.createdAt));
  const [noticeIndex, setNoticeIndex] = useState(0);
  const currentNotice =
    notifications[noticeIndex % Math.max(1, notifications.length)];

  async function setTaskCompleted(task: Task, completed: boolean) {
    const previous = { ...task };
    await saveEntity(
      "task",
      {
        ...task,
        completed,
        completedAt: completed ? new Date().toISOString() : undefined,
        updatedAt: new Date().toISOString(),
      },
      {
        activityTitle: completed
          ? "Task completed on iPhone"
          : "Task reopened on iPhone",
        activityDetail: task.title,
      },
    );
    addNotice(
      completed
        ? {
            title: "Task completed",
            body: task.title,
            actionLabel: "Undo",
            onAction: () =>
              void saveEntity(
                "task",
                {
                  ...previous,
                  completed: false,
                  completedAt: undefined,
                  updatedAt: new Date().toISOString(),
                },
                {
                  activityTitle: "Task completion undone on iPhone",
                  activityDetail: task.title,
                },
              ),
          }
        : { title: "Task reopened", body: task.title },
    );
  }

  const filteredTasks = data.tasks
    .filter((task) => {
      const matchesSearch =
        `${task.title} ${matterName(data.matters, task.matterId)} ${task.notes || ""}`
          .toLowerCase()
          .includes(taskSearch.toLowerCase());
      if (!matchesSearch) return false;
      if (taskFilter === "completed") return Boolean(task.completed);
      if (task.completed) return false;
      if (taskFilter === "today") return task.dueDate === todayIso();
      if (taskFilter === "overdue")
        return (daysFromToday(task.dueDate) ?? 1) < 0;
      return true;
    })
    .sort(
      (a, b) =>
        Number(Boolean(a.completed)) - Number(Boolean(b.completed)) ||
        recordDate(a.dueDate) - recordDate(b.dueDate) ||
        priorityWeight(b.priority) - priorityWeight(a.priority),
    );

  const taskRow = (task: Task) => (
    <article
      className={`task-manager-row md-glass ${task.completed ? "completed" : ""}`}
      key={task.id}
    >
      <button
        className={`complete-circle ${task.completed ? "is-complete" : ""}`}
        onClick={() => void setTaskCompleted(task, !task.completed)}
        aria-label={task.completed ? "Reopen task" : "Complete task"}
        aria-pressed={Boolean(task.completed)}
      >
        <Icon name={task.completed ? "sync" : "check"} />
      </button>
      <button
        className="task-manager-main"
        onClick={() => openCapture("task", task)}
      >
        <div className="item-title-line">
          <strong>{task.title}</strong>
          <PriorityBadge priority={task.priority} />
        </div>
        <span>
          {task.dueDate
            ? `${formatDate(task.dueDate)} · ${countdownLabel(task.dueDate)}`
            : "No due date"}
          {matterName(data.matters, task.matterId)
            ? ` · ${matterName(data.matters, task.matterId)}`
            : ""}
        </span>
      </button>
      <button
        className="task-text-action"
        onClick={() => void setTaskCompleted(task, !task.completed)}
      >
        {task.completed ? "Reopen" : "Complete"}
      </button>
    </article>
  );

  return (
    <div className="page-stack dashboard-page">
      <div className="page-toolbar md-glass dashboard-mode">
        <Segmented
          value={view}
          onChange={setView}
          options={[
            { value: "today", label: "Today" },
            { value: "tasks", label: "Tasks" },
          ]}
        />
        <button className="primary-mini" onClick={() => openCapture("task")}>
          <Icon name="plus" /> Task
        </button>
      </div>
      {view === "tasks" ? (
        <>
          <section className="task-summary-strip md-glass">
            <span>
              <strong>{activeTasks.length}</strong> open
            </span>
            <span>
              <strong>{overdue.length}</strong> overdue
            </span>
            <span>
              <strong>
                {data.tasks.filter((item) => item.completed).length}
              </strong>{" "}
              completed
            </span>
          </section>
          <section className="task-manager-tools md-glass">
            <div className="search-field">
              <Icon name="search" />
              <input
                placeholder="Search tasks"
                value={taskSearch}
                onChange={(event) => setTaskSearch(event.target.value)}
              />
            </div>
            <div className="task-filter-scroll">
              {(["open", "today", "overdue", "completed"] as const).map(
                (value) => (
                  <button
                    key={value}
                    className={taskFilter === value ? "active" : ""}
                    onClick={() => setTaskFilter(value)}
                  >
                    {value[0].toUpperCase() + value.slice(1)}
                  </button>
                ),
              )}
            </div>
          </section>
          <div className="task-manager-list">
            {filteredTasks.map(taskRow)}
            {!filteredTasks.length && (
              <EmptyState
                title="No matching tasks"
                detail="Change the filter or create a new task."
              />
            )}
          </div>
        </>
      ) : (
        <>
          <section className="attention-carousel md-glass">
            <span
              className={`attention-dot ${notifications.length ? "active" : ""}`}
            />
            <button
              className="attention-message"
              onClick={() =>
                currentNotice &&
                openEntity(currentNotice.entityType, currentNotice.entityId)
              }
            >
              {currentNotice ? (
                <>
                  <strong>{currentNotice.title}</strong>
                  <span>{currentNotice.body || currentNotice.detail}</span>
                </>
              ) : (
                <span>No active notifications</span>
              )}
            </button>
            <div className="attention-controls">
              <button
                onClick={() =>
                  setNoticeIndex((index) => Math.max(0, index - 1))
                }
                aria-label="Previous"
              >
                <Icon name="arrowUp" />
              </button>
              <button
                onClick={() => setNoticeIndex((index) => index + 1)}
                aria-label="Next"
              >
                <Icon name="arrowDown" />
              </button>
            </div>
          </section>

          <section className="summary-grid">
            <GlassCard>
              <span className="summary-value">{overdue.length}</span>
              <span>Overdue</span>
            </GlassCard>
            <GlassCard>
              <span className="summary-value">{todayTasks.length}</span>
              <span>Due today</span>
            </GlassCard>
            <GlassCard>
              <span className="summary-value">{dueKiv.length}</span>
              <span>KIV reviews</span>
            </GlassCard>
          </section>

          <section className="section-block">
            <header className="section-title">
              <div>
                <span className="eyebrow">QUICK CAPTURE</span>
                <h2>Add something</h2>
              </div>
            </header>
            <div className="quick-grid">
              {[
                ["task", "Task"],
                ["event", "Event"],
                ["date", "Important Date"],
                ["kiv", "KIV"],
              ].map(([type, label]) => (
                <button
                  key={type}
                  className="quick-card md-glass"
                  onClick={() => openCapture(type as CaptureType)}
                >
                  <Icon
                    name={
                      type === "event" || type === "date"
                        ? "calendar"
                        : type === "kiv"
                          ? "kiv"
                          : "check"
                    }
                  />
                  <span>+ {label}</span>
                </button>
              ))}
            </div>
          </section>

          {(overdue.length > 0 || todayTasks.length > 0) && (
            <section className="section-block">
              <header className="section-title">
                <div>
                  <span className="eyebrow">TASKS</span>
                  <h2>Needs attention</h2>
                </div>
                <button className="text-link" onClick={() => setView("tasks")}>
                  View all
                </button>
              </header>
              <div className="item-list">
                {[
                  ...overdue,
                  ...todayTasks.filter((item) => !overdue.includes(item)),
                ]
                  .slice(0, 6)
                  .map(taskRow)}
              </div>
            </section>
          )}

          <section className="section-block">
            <header className="section-title">
              <div>
                <span className="eyebrow">UPCOMING</span>
                <h2>Overall timeline</h2>
              </div>
              <span className="count-badge">{upcoming.length}</span>
            </header>
            {upcoming.length ? (
              <div className="timeline-list">
                {upcoming.map((item) => (
                  <button
                    key={`${item.type}-${item.id}`}
                    className={`timeline-row md-glass timeline-${String(item.priority || "").toLowerCase()}`}
                    onClick={() => openEntity(item.type, item.id)}
                  >
                    <div className="timeline-date">
                      <strong>
                        {formatDate(item.date, { year: undefined })}
                      </strong>
                      <span>{countdownLabel(item.date)}</span>
                    </div>
                    <div className="timeline-main">
                      <span className="timeline-kind">{item.kind}</span>
                      <div>
                        <strong>{item.title}</strong>
                        <small>{matterName(data.matters, item.matterId)}</small>
                      </div>
                    </div>
                    {String(item.priority).toLowerCase() !== "normal" && (
                      <PriorityBadge priority={item.priority} />
                    )}
                  </button>
                ))}
              </div>
            ) : (
              <EmptyState
                title="Nothing upcoming"
                detail="Create a task, date, event or KIV item."
              />
            )}
          </section>
        </>
      )}
    </div>
  );
}

function matterHealth(
  matter: Matter,
  data: ReturnType<typeof useStore>["data"],
): { label: string; colour: string; next?: string } {
  const tasks = data.tasks.filter(
    (item) => item.matterId === matter.id && !item.completed,
  );
  const overdue = tasks.filter(
    (item) => (daysFromToday(item.dueDate) ?? 1) < 0,
  ).length;
  const dates = [
    ...data.events
      .filter((item) => item.matterId === matter.id)
      .map((item) => item.date),
    ...data.importantDates
      .filter((item) => item.matterId === matter.id && !item.completed)
      .map((item) => item.date),
    ...data.kivItems
      .filter((item) => item.matterId === matter.id && item.status !== "Closed")
      .map((item) => item.reviewDate),
  ]
    .filter(Boolean)
    .sort();
  const next = dates.find((date) => (daysFromToday(date) ?? -1) >= 0);
  if (overdue > 0) return { label: "Needs attention", colour: "#dc4a4a", next };
  if (next && (daysFromToday(next) ?? 99) <= 7)
    return { label: "Watch", colour: "#e49b2d", next };
  return { label: "Healthy", colour: "#2da878", next };
}

function MattersPage({
  selectedMatter,
  setSelectedMatter,
  openCapture,
}: {
  selectedMatter: string | null;
  setSelectedMatter: (id: string | null) => void;
  openCapture: (
    type: CaptureType,
    entity?: Task | CalendarEvent | ImportantDate | KivItem,
  ) => void;
}) {
  const { data } = useStore();
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<"active" | "closed">("active");
  const matters = data.matters
    .filter((matter) => {
      const status = String(matter.status || "").toLowerCase();
      const active =
        ACTIVE_MATTER_STATUSES.has(status) ||
        !["closed", "completed", "archived"].includes(status);
      return (
        (filter === "active" ? active : !active) &&
        `${matter.name} ${matter.reference || ""}`
          .toLowerCase()
          .includes(search.toLowerCase())
      );
    })
    .sort((a, b) => a.name.localeCompare(b.name));
  const selected =
    data.matters.find((matter) => matter.id === selectedMatter) || null;
  return (
    <div className="page-stack">
      <div className="page-toolbar md-glass">
        <div className="search-field">
          <Icon name="search" />
          <input
            placeholder="Search matters"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>
        <Segmented
          value={filter}
          onChange={setFilter}
          options={[
            { value: "active", label: "Active" },
            { value: "closed", label: "Closed" },
          ]}
        />
      </div>
      <div className="matter-list">
        {matters.map((matter) => {
          const health = matterHealth(matter, data);
          return (
            <button
              key={matter.id}
              className="matter-health-card md-glass"
              style={
                {
                  "--health-colour": health.colour,
                  "--matter-colour": matter.colour || "#2b5bb7",
                } as React.CSSProperties
              }
              onClick={() => setSelectedMatter(matter.id)}
            >
              <div className="matter-copy">
                <strong>{matter.name}</strong>
                <span>{matter.reference || "No reference"}</span>
                <small>
                  {health.label}
                  {health.next ? ` · ${formatDate(health.next)}` : ""}
                </small>
              </div>
              <Icon name="chevron" />
            </button>
          );
        })}
        {!matters.length && <EmptyState title="No matching matters" />}
      </div>
      <MatterSheet
        matter={selected}
        onClose={() => setSelectedMatter(null)}
        openCapture={openCapture}
      />
    </div>
  );
}

function MatterSheet({
  matter,
  onClose,
  openCapture,
}: {
  matter: Matter | null;
  onClose: () => void;
  openCapture: (
    type: CaptureType,
    entity?: Task | CalendarEvent | ImportantDate | KivItem,
  ) => void;
}) {
  const { data, saveEntity } = useStore();
  const [tab, setTab] = useState<
    "overview" | "tasks" | "dates" | "kiv" | "activity"
  >("overview");
  useEffect(() => {
    if (matter) setTab("overview");
  }, [matter]);
  if (!matter) return null;
  const tasks = data.tasks.filter(
    (item) => item.matterId === matter.id && !item.completed,
  );
  const events = data.events.filter((item) => item.matterId === matter.id);
  const dates = data.importantDates.filter(
    (item) => item.matterId === matter.id && !item.completed,
  );
  const kiv = data.kivItems.filter(
    (item) => item.matterId === matter.id && item.status !== "Closed",
  );
  const activity = data.activity
    .filter((item) => item.matterId === matter.id)
    .sort((a, b) => recordDate(b.createdAt) - recordDate(a.createdAt));
  const health = matterHealth(matter, data);
  return (
    <Sheet open title={matter.name} onClose={onClose} large>
      <div
        className="matter-sheet-head"
        style={
          {
            "--health-colour": health.colour,
            "--matter-colour": matter.colour || "#2b5bb7",
          } as React.CSSProperties
        }
      >
        <span>{matter.reference || "No reference"}</span>
        <strong>{health.label}</strong>
      </div>
      <div className="scroll-tabs">
        {(["overview", "tasks", "dates", "kiv", "activity"] as const).map(
          (value) => (
            <button
              className={tab === value ? "active" : ""}
              key={value}
              onClick={() => setTab(value)}
            >
              {value[0].toUpperCase() + value.slice(1)}
            </button>
          ),
        )}
      </div>
      {tab === "overview" && (
        <div className="matter-overview-grid">
          <GlassCard>
            <span>Open tasks</span>
            <strong>{tasks.length}</strong>
          </GlassCard>
          <GlassCard>
            <span>Upcoming dates</span>
            <strong>{events.length + dates.length}</strong>
          </GlassCard>
          <GlassCard>
            <span>Open KIV</span>
            <strong>{kiv.length}</strong>
          </GlassCard>
          <GlassCard>
            <span>Next action</span>
            <strong>
              {tasks.sort(
                (a, b) => recordDate(a.dueDate) - recordDate(b.dueDate),
              )[0]?.title || "None"}
            </strong>
          </GlassCard>
        </div>
      )}
      {tab === "tasks" && (
        <div className="item-list">
          {tasks.map((item) => (
            <article className="task-manager-row md-glass" key={item.id}>
              <button
                className="complete-circle"
                onClick={() =>
                  void saveEntity(
                    "task",
                    {
                      ...item,
                      completed: true,
                      completedAt: new Date().toISOString(),
                      updatedAt: new Date().toISOString(),
                    },
                    {
                      activityTitle: "Task completed on iPhone",
                      activityDetail: item.title,
                    },
                  )
                }
                aria-label="Complete task"
              >
                <Icon name="check" />
              </button>
              <button
                className="task-manager-main"
                onClick={() => openCapture("task", item)}
              >
                <div className="item-title-line">
                  <strong>{item.title}</strong>
                  <PriorityBadge priority={item.priority} />
                </div>
                <span>
                  {formatDate(item.dueDate)} · {countdownLabel(item.dueDate)}
                </span>
              </button>
              <button
                className="task-text-action"
                onClick={() =>
                  void saveEntity(
                    "task",
                    {
                      ...item,
                      completed: true,
                      completedAt: new Date().toISOString(),
                      updatedAt: new Date().toISOString(),
                    },
                    {
                      activityTitle: "Task completed on iPhone",
                      activityDetail: item.title,
                    },
                  )
                }
              >
                Complete
              </button>
            </article>
          ))}
          {!tasks.length && <EmptyState title="No open tasks" />}
        </div>
      )}
      {tab === "dates" && (
        <div className="item-list">
          {[
            ...events.map((item) => ({ ...item, _type: "event" })),
            ...dates.map((item) => ({ ...item, _type: "date" })),
          ]
            .sort((a, b) => recordDate(a.date) - recordDate(b.date))
            .map((item) => (
              <button
                className="compact-item md-glass"
                key={`${item._type}-${item.id}`}
                onClick={() =>
                  openCapture(
                    item._type as CaptureType,
                    item as CalendarEvent | ImportantDate,
                  )
                }
              >
                <div>
                  <strong>{item.title}</strong>
                  <span>
                    {formatDate(item.date)} · {countdownLabel(item.date)}
                  </span>
                </div>
              </button>
            ))}
          {!events.length && !dates.length && (
            <EmptyState title="No upcoming dates" />
          )}
        </div>
      )}
      {tab === "kiv" && (
        <div className="item-list">
          {kiv.map((item) => (
            <button
              className="compact-item md-glass"
              key={item.id}
              onClick={() => openCapture("kiv", item)}
            >
              <div>
                <strong>{item.title}</strong>
                <span>
                  {formatDate(item.reviewDate)} · {item.recurrence || "One-off"}
                </span>
              </div>
              <PriorityBadge priority={item.priority} />
            </button>
          ))}
          {!kiv.length && <EmptyState title="No open KIV" />}
        </div>
      )}
      {tab === "activity" && (
        <div className="activity-list">
          {activity.map((item) => (
            <article className="activity-row" key={item.id}>
              <span className="activity-dot" />
              <div>
                <strong>{item.title}</strong>
                <span>{item.detail || item.body}</span>
                <small>
                  {item.createdAt
                    ? new Date(item.createdAt).toLocaleString("en-GB")
                    : ""}
                </small>
              </div>
            </article>
          ))}
          {!activity.length && <EmptyState title="No recent activity" />}
        </div>
      )}
    </Sheet>
  );
}

function CalendarPage({
  openCapture,
}: {
  openCapture: (
    type: CaptureType,
    entity?: Task | CalendarEvent | ImportantDate | KivItem,
  ) => void;
}) {
  const { data } = useStore();
  const [view, setView] = useState<"agenda" | "month">("agenda");
  const [month, setMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(todayIso());
  const rows = useMemo(
    () =>
      [
        ...data.events.map((item) => ({
          id: item.id,
          title: item.title,
          date: item.date,
          endDate: item.endDate,
          time: item.time,
          matterId: item.matterId,
          category: item.category || "Calendar",
          priority: "Normal",
          type: "event" as CaptureType,
          entity: item,
        })),
        ...data.importantDates
          .filter((item) => !item.completed)
          .map((item) => ({
            id: item.id,
            title: item.title,
            date: item.date,
            endDate: undefined as string | undefined,
            time: item.time,
            matterId: item.matterId,
            category:
              item.dateType || item.type || item.category || "Important Date",
            priority: item.priority,
            type: "date" as CaptureType,
            entity: item,
          })),
      ].sort(
        (a, b) =>
          recordDate(a.date) - recordDate(b.date) ||
          String(a.time).localeCompare(String(b.time)),
      ),
    [data.events, data.importantDates],
  );
  const agenda = rows.filter((item) => (daysFromToday(item.date) ?? -1) >= -30);
  const selectedRows = rows.filter(
    (item) =>
      item.date === selectedDate ||
      (item.endDate &&
        item.date <= selectedDate &&
        item.endDate >= selectedDate),
  );
  const grid = monthGrid(month);
  return (
    <div className="page-stack">
      <div className="page-toolbar md-glass">
        <Segmented
          value={view}
          onChange={setView}
          options={[
            { value: "agenda", label: "Agenda" },
            { value: "month", label: "Month" },
          ]}
        />
        <button className="primary-mini" onClick={() => openCapture("event")}>
          <Icon name="plus" /> Add
        </button>
      </div>
      {view === "agenda" ? (
        <div className="agenda-list">
          {agenda.map((item) => (
            <button
              key={`${item.type}-${item.id}`}
              className="agenda-row md-glass"
              onClick={() => openCapture(item.type, item.entity)}
            >
              <div className="agenda-date">
                <strong>{new Date(`${item.date}T12:00:00`).getDate()}</strong>
                <span>
                  {new Intl.DateTimeFormat("en-GB", { month: "short" }).format(
                    new Date(`${item.date}T12:00:00`),
                  )}
                </span>
              </div>
              <div className="item-copy">
                <span className="timeline-kind">{item.category}</span>
                <strong>{item.title}</strong>
                <small>
                  {formatTime(item.time, data.preferences.timeFormat)}
                  {matterName(data.matters, item.matterId)
                    ? ` · ${matterName(data.matters, item.matterId)}`
                    : ""}
                </small>
              </div>
              {String(item.priority).toLowerCase() !== "normal" && (
                <PriorityBadge priority={item.priority} />
              )}
            </button>
          ))}
          {!agenda.length && <EmptyState title="No calendar dates" />}
        </div>
      ) : (
        <>
          <section className="month-card md-glass">
            <header>
              <button
                onClick={() =>
                  setMonth(
                    (value) =>
                      new Date(value.getFullYear(), value.getMonth() - 1, 1),
                  )
                }
              >
                ‹
              </button>
              <strong>
                {new Intl.DateTimeFormat("en-GB", {
                  month: "long",
                  year: "numeric",
                }).format(month)}
              </strong>
              <button
                onClick={() =>
                  setMonth(
                    (value) =>
                      new Date(value.getFullYear(), value.getMonth() + 1, 1),
                  )
                }
              >
                ›
              </button>
            </header>
            <div className="weekdays">
              {["M", "T", "W", "T", "F", "S", "S"].map((day, index) => (
                <span key={`${day}-${index}`}>{day}</span>
              ))}
            </div>
            <div className="month-grid">
              {grid.map((date) => {
                const iso = toIsoDate(date);
                const count = rows.filter(
                  (item) =>
                    item.date === iso ||
                    (item.endDate && item.date <= iso && item.endDate >= iso),
                ).length;
                return (
                  <button
                    key={iso}
                    className={`${date.getMonth() !== month.getMonth() ? "muted" : ""} ${selectedDate === iso ? "selected" : ""} ${iso === todayIso() ? "today" : ""}`}
                    onClick={() => setSelectedDate(iso)}
                  >
                    <span>{date.getDate()}</span>
                    {count > 0 && <i>{Math.min(count, 4)}</i>}
                  </button>
                );
              })}
            </div>
          </section>
          <section className="section-block">
            <header className="section-title">
              <div>
                <span className="eyebrow">{formatDate(selectedDate)}</span>
                <h2>Schedule</h2>
              </div>
            </header>
            <div className="item-list">
              {selectedRows.map((item) => (
                <button
                  className="compact-item md-glass"
                  key={`${item.type}-${item.id}`}
                  onClick={() => openCapture(item.type, item.entity)}
                >
                  <div>
                    <strong>{item.title}</strong>
                    <span>
                      {item.category}
                      {item.time
                        ? ` · ${formatTime(item.time, data.preferences.timeFormat)}`
                        : ""}
                    </span>
                  </div>
                </button>
              ))}
              {!selectedRows.length && (
                <EmptyState title="No entries for this date" />
              )}
            </div>
          </section>
        </>
      )}
    </div>
  );
}

function KivPage({
  openCapture,
  addNotice,
}: {
  openCapture: (
    type: CaptureType,
    entity?: Task | CalendarEvent | ImportantDate | KivItem,
  ) => void;
  addNotice: (notice: Omit<GlassNotice, "id"> & { id?: string }) => void;
}) {
  const { data, saveEntity } = useStore();
  const [filter, setFilter] = useState<"due" | "upcoming" | "closed">("due");
  const rows = data.kivItems
    .filter((item) => {
      const closed = String(item.status || "").toLowerCase() === "closed";
      const days = daysFromToday(item.reviewDate) ?? 0;
      return filter === "closed"
        ? closed
        : filter === "due"
          ? !closed && days <= 0
          : !closed && days > 0;
    })
    .sort((a, b) => recordDate(a.reviewDate) - recordDate(b.reviewDate));
  async function review(item: KivItem) {
    const recurring = item.recurrence && item.recurrence !== "none";
    const updated = {
      ...item,
      lastReviewed: new Date().toISOString(),
      reviewDate: recurring
        ? nextRecurringDate(item.reviewDate, item.recurrence)
        : item.reviewDate,
      status: recurring ? "Open" : "Closed",
      updatedAt: new Date().toISOString(),
    };
    await saveEntity("kiv", updated, {
      activityTitle: recurring
        ? "KIV reviewed on iPhone"
        : "KIV closed on iPhone",
      activityDetail: item.title,
    });
    addNotice({
      title: recurring ? "KIV reviewed" : "KIV closed",
      body: recurring
        ? `Next review: ${formatDate(updated.reviewDate)}`
        : item.title,
    });
  }
  async function snooze(item: KivItem, days: number) {
    const next = new Date(`${item.reviewDate}T12:00:00`);
    next.setDate(next.getDate() + days);
    await saveEntity(
      "kiv",
      {
        ...item,
        reviewDate: toIsoDate(next),
        updatedAt: new Date().toISOString(),
      },
      {
        activityTitle: "KIV rescheduled on iPhone",
        activityDetail: item.title,
      },
    );
  }
  return (
    <div className="page-stack">
      <div className="page-toolbar md-glass">
        <Segmented
          value={filter}
          onChange={setFilter}
          options={[
            { value: "due", label: "Due" },
            { value: "upcoming", label: "Upcoming" },
            { value: "closed", label: "Closed" },
          ]}
        />
        <button className="primary-mini" onClick={() => openCapture("kiv")}>
          <Icon name="plus" /> Add
        </button>
      </div>
      <div className="kiv-list">
        {rows.map((item) => (
          <article className="kiv-card md-glass" key={item.id}>
            <button
              className="kiv-main"
              onClick={() => openCapture("kiv", item)}
            >
              <div className="item-title-line">
                <strong>{item.title}</strong>
                <PriorityBadge priority={item.priority} />
              </div>
              <span>
                {matterName(data.matters, item.matterId) ||
                  "No matter selected"}
              </span>
              <div className="kiv-meta">
                <b>{formatDate(item.reviewDate)}</b>
                <span>{countdownLabel(item.reviewDate)}</span>
                <span>
                  {item.recurrence && item.recurrence !== "none"
                    ? item.recurrence
                    : "One-off"}
                </span>
              </div>
            </button>
            {filter !== "closed" && (
              <div className="card-actions">
                <button onClick={() => void snooze(item, 1)}>+1 day</button>
                <button onClick={() => void snooze(item, 7)}>+1 week</button>
                <button className="primary" onClick={() => void review(item)}>
                  {item.recurrence && item.recurrence !== "none"
                    ? "Mark Reviewed"
                    : "Close KIV"}
                </button>
              </div>
            )}
          </article>
        ))}
        {!rows.length && (
          <EmptyState
            title={
              filter === "due"
                ? "No KIV reviews due"
                : filter === "upcoming"
                  ? "No upcoming KIV"
                  : "No closed KIV"
            }
          />
        )}
      </div>
    </div>
  );
}

function ActivityPage({
  openEntity,
  completeSource,
  snoozeSource,
}: {
  openEntity: (type?: string, id?: string) => void;
  completeSource: (type: string, id: string) => Promise<void>;
  snoozeSource: (type: string, id: string, minutes?: number) => Promise<void>;
}) {
  const { data, saveEntity } = useStore();
  const [tab, setTab] = useState<"notifications" | "activity">("notifications");
  const notifications = data.notifications
    .filter((item) => !item.archived)
    .sort((a, b) => recordDate(b.createdAt) - recordDate(a.createdAt));
  const activity = data.activity.sort(
    (a, b) => recordDate(b.createdAt) - recordDate(a.createdAt),
  );
  async function updateNotification(
    item: NotificationItem,
    patch: Partial<NotificationItem>,
  ) {
    await saveEntity(
      "notification",
      { ...item, ...patch, updatedAt: new Date().toISOString() },
      { skipActivity: true },
    );
  }
  return (
    <div className="page-stack">
      <div className="page-toolbar md-glass">
        <Segmented
          value={tab}
          onChange={setTab}
          options={[
            { value: "notifications", label: "Notifications" },
            { value: "activity", label: "Activity" },
          ]}
        />
      </div>
      {tab === "notifications" ? (
        <div className="notification-centre-list">
          {notifications.map((item) => (
            <article
              className={`centre-notification md-glass ${item.read ? "read" : ""}`}
              key={item.id}
            >
              <button
                className="notification-main"
                onClick={() => {
                  void updateNotification(item, {
                    read: true,
                    acknowledgedAt: new Date().toISOString(),
                  });
                  openEntity(item.entityType, item.entityId);
                }}
              >
                <span className="notification-bullet" />
                <div>
                  <strong>{item.title}</strong>
                  <span>{item.body || item.detail}</span>
                  <small>
                    {item.createdAt
                      ? new Date(item.createdAt).toLocaleString("en-GB")
                      : ""}
                  </small>
                </div>
              </button>
              <div className="card-actions">
                <button
                  onClick={() =>
                    void updateNotification(item, {
                      read: true,
                      acknowledgedAt: new Date().toISOString(),
                    })
                  }
                >
                  Acknowledge
                </button>
                {item.entityId && (
                  <button
                    onClick={() =>
                      void snoozeSource(
                        item.entityType || "",
                        item.entityId!,
                        60,
                      )
                    }
                  >
                    Snooze
                  </button>
                )}
                {item.entityId &&
                  ["task", "kiv", "important_date"].includes(
                    item.entityType || "",
                  ) && (
                    <button
                      className="primary"
                      onClick={() =>
                        void completeSource(
                          item.entityType || "",
                          item.entityId || "",
                        )
                      }
                    >
                      Complete
                    </button>
                  )}
                <button
                  onClick={() =>
                    void updateNotification(item, { archived: true })
                  }
                >
                  Archive
                </button>
              </div>
            </article>
          ))}
          {!notifications.length && (
            <EmptyState title="No active notifications" />
          )}
        </div>
      ) : (
        <div className="activity-list md-glass activity-panel">
          {activity.map((item) => (
            <article className="activity-row" key={item.id}>
              <span className="activity-dot" />
              <div>
                <strong>{item.title}</strong>
                <span>{item.detail || item.body}</span>
                <small>
                  {item.createdAt
                    ? new Date(item.createdAt).toLocaleString("en-GB")
                    : ""}
                </small>
              </div>
            </article>
          ))}
          {!activity.length && <EmptyState title="No activity yet" />}
        </div>
      )}
    </div>
  );
}

function SettingsSheet({
  open,
  onClose,
  addNotice,
}: {
  open: boolean;
  onClose: () => void;
  addNotice: (notice: Omit<GlassNotice, "id"> & { id?: string }) => void;
}) {
  const {
    data,
    user,
    online,
    syncing,
    lastSync,
    pendingCount,
    realtimeStatus,
    error,
    saveEntity,
    syncNow,
    signOut,
    clearDeviceData,
  } = useStore();
  const [prefs, setPrefs] = useState<MobilePreferences>(data.preferences);
  const [status, setStatus] = useState("");
  useEffect(() => setPrefs(data.preferences), [data.preferences]);
  async function savePrefs(next: MobilePreferences) {
    setPrefs(next);
    await saveEntity(
      "mobile_preferences",
      {
        ...next,
        id: "mobile-preferences",
        updatedAt: new Date().toISOString(),
      },
      { skipActivity: true },
    );
  }
  const patch = (next: Partial<MobilePreferences>) =>
    void savePrefs({ ...prefs, ...next });
  async function testCombined() {
    setStatus("Testing…");
    addNotice({
      title: "Matter Dashboard",
      body: "Your MD Glass in-app notification is working.",
    });
    if (prefs.soundEnabled) await playTabletopBell();
    const system = await showSystemNotification({
      title: "Matter Dashboard",
      body: "Your iPhone notification is working.",
      tag: `mdm-test-${Date.now()}`,
      url: "/?page=activity",
    });
    setStatus(
      system
        ? "In-app and browser notifications sent."
        : "In-app sent. Browser permission is not enabled.",
    );
  }
  async function enablePush() {
    setStatus("Enabling push…");
    try {
      const result = await subscribeToPush();
      setStatus(result.message);
      if (result.subscribed) await savePrefs({ ...prefs, pushEnabled: true });
    } catch (pushError) {
      setStatus(
        pushError instanceof Error ? pushError.message : String(pushError),
      );
    }
  }
  async function remoteTest() {
    setStatus("Sending remote push…");
    try {
      const result = await testRemotePush();
      setStatus(result.message);
    } catch (pushError) {
      setStatus(
        pushError instanceof Error ? pushError.message : String(pushError),
      );
    }
  }
  return (
    <Sheet open={open} title="Settings" onClose={onClose} large>
      <div className="settings-stack">
        <section className="settings-section">
          <div className="settings-heading">
            <div>
              <span className="eyebrow">ACCOUNT</span>
              <h3>{user?.email}</h3>
            </div>
            <span className={`status-pill ${online ? "good" : "warn"}`}>
              {online ? "Online" : "Offline"}
            </span>
          </div>
          <div className="settings-actions">
            <button onClick={() => void syncNow()} disabled={syncing}>
              <Icon name="sync" />
              {syncing ? "Syncing…" : "Sync now"}
            </button>
            <button onClick={() => void clearDeviceData()}>
              Refresh local cache
            </button>
          </div>
          <div className="sync-detail-grid">
            <small>
              Last sync:{" "}
              {lastSync ? new Date(lastSync).toLocaleString("en-GB") : "Never"}
            </small>
            <small>
              Mode:{" "}
              {realtimeStatus === "live"
                ? "Realtime live"
                : realtimeStatus === "fallback"
                  ? "Automatic refresh"
                  : realtimeStatus === "connecting"
                    ? "Connecting"
                    : "Offline"}
              {pendingCount ? ` · ${pendingCount} queued` : ""}
            </small>
          </div>
          {error && <div className="form-error">{error}</div>}
        </section>
        <section className="settings-section">
          <div className="settings-heading">
            <div>
              <span className="eyebrow">APPEARANCE</span>
              <h3>MD Glass</h3>
            </div>
          </div>
          <label>
            <span>Theme</span>
            <select
              value={prefs.theme}
              onChange={(event) =>
                patch({
                  theme: event.target.value as MobilePreferences["theme"],
                })
              }
            >
              <option value="system">System</option>
              <option value="light">Light</option>
              <option value="dark">Dark</option>
            </select>
          </label>
          <Toggle
            checked={prefs.reducedMotion}
            onChange={(value) => patch({ reducedMotion: value })}
            label="Reduce Motion"
          />
          <Toggle
            checked={prefs.reducedTransparency}
            onChange={(value) => patch({ reducedTransparency: value })}
            label="Reduce Transparency"
          />
          <label>
            <span>Time format</span>
            <select
              value={prefs.timeFormat}
              onChange={(event) =>
                patch({ timeFormat: event.target.value as "12" | "24" })
              }
            >
              <option value="24">24-hour</option>
              <option value="12">12-hour</option>
            </select>
          </label>
        </section>
        <section className="settings-section">
          <div className="settings-heading">
            <div>
              <span className="eyebrow">NOTIFICATIONS</span>
              <h3>Delivery</h3>
            </div>
          </div>
          <Toggle
            checked={prefs.notificationsEnabled}
            onChange={(value) => patch({ notificationsEnabled: value })}
            label="Notifications"
          />
          <Toggle
            checked={prefs.inAppEnabled}
            onChange={(value) => patch({ inAppEnabled: value })}
            label="MD Glass in-app"
          />
          <Toggle
            checked={prefs.pushEnabled}
            onChange={(value) => patch({ pushEnabled: value })}
            label="iPhone Web Push"
          />
          <Toggle
            checked={prefs.soundEnabled}
            onChange={(value) => patch({ soundEnabled: value })}
            label="Tabletop bell"
          />
          <Toggle
            checked={prefs.showMissedOnRestore}
            onChange={(value) => patch({ showMissedOnRestore: value })}
            label="Show missed reminders when restored"
          />
          <Toggle
            checked={prefs.quietEnabled}
            onChange={(value) => patch({ quietEnabled: value })}
            label="Quiet hours"
          />
          {prefs.quietEnabled && (
            <div className="form-grid two">
              <label>
                <span>From</span>
                <input
                  type="time"
                  value={prefs.quietStart}
                  onChange={(event) =>
                    patch({ quietStart: event.target.value })
                  }
                />
              </label>
              <label>
                <span>Until</span>
                <input
                  type="time"
                  value={prefs.quietEnd}
                  onChange={(event) => patch({ quietEnd: event.target.value })}
                />
              </label>
            </div>
          )}
          <div className="settings-actions">
            <button className="primary" onClick={() => void testCombined()}>
              Test in-app + iPhone
            </button>
            <button onClick={() => void enablePush()}>Enable Web Push</button>
            <button onClick={() => void remoteTest()}>
              Test background push
            </button>
          </div>
          {status && <div className="status-message">{status}</div>}
        </section>
        <section className="settings-section">
          <div className="settings-heading">
            <div>
              <span className="eyebrow">REMINDER DEFAULTS</span>
              <h3>Shared with desktop</h3>
            </div>
          </div>
          <div className="form-grid two">
            <label>
              <span>Mode</span>
              <select
                value={prefs.defaultReminderMode}
                onChange={(event) =>
                  patch({
                    defaultReminderMode: event.target
                      .value as MobilePreferences["defaultReminderMode"],
                  })
                }
              >
                <option value="none">None</option>
                <option value="once">Once</option>
                <option value="daily">Daily</option>
                <option value="repeat">Repeating</option>
                <option value="persistent">Persistent until completed</option>
              </select>
            </label>
            <label>
              <span>Before due</span>
              <select
                value={prefs.defaultLeadMinutes}
                onChange={(event) =>
                  patch({ defaultLeadMinutes: Number(event.target.value) })
                }
              >
                <option value={0}>At time</option>
                <option value={15}>15 minutes</option>
                <option value={30}>30 minutes</option>
                <option value={60}>1 hour</option>
                <option value={1440}>1 day</option>
              </select>
            </label>
            <label>
              <span>Repeat interval</span>
              <input
                type="number"
                min="15"
                step="5"
                value={prefs.defaultRepeatMinutes}
                onChange={(event) =>
                  patch({
                    defaultRepeatMinutes: Math.max(
                      15,
                      Number(event.target.value) || 15,
                    ),
                  })
                }
              />
            </label>
            <label>
              <span>Daily time</span>
              <input
                type="time"
                value={prefs.defaultDailyTime}
                onChange={(event) =>
                  patch({ defaultDailyTime: event.target.value })
                }
              />
            </label>
          </div>
        </section>
        <section className="settings-section">
          <div className="settings-heading">
            <div>
              <span className="eyebrow">INSTALLATION</span>
              <h3>iPhone Home Screen</h3>
            </div>
          </div>
          <p>
            Open the deployed app in Safari, tap Share, then Add to Home Screen.
            Notifications require the installed Home Screen app on iOS 16.4 or
            later.
          </p>
        </section>
        <button className="danger-button full" onClick={() => void signOut()}>
          Sign out
        </button>
      </div>
    </Sheet>
  );
}

function useReminderEngine({
  session,
  addGlassNotice,
  completeSource,
  snoozeSource,
  openEntity,
}: {
  session: Session | null;
  addGlassNotice: (notice: Omit<GlassNotice, "id"> & { id?: string }) => void;
  completeSource: (type: string, id: string) => Promise<void>;
  snoozeSource: (type: string, id: string, minutes?: number) => Promise<void>;
  openEntity: (type?: string, id?: string) => void;
}) {
  const { data, saveEntity } = useStore();
  const check = useCallback(async () => {
    if (!session || !data.preferences.notificationsEnabled) return;
    const now = new Date();
    const time = now.toTimeString().slice(0, 5);
    const p = data.preferences;
    const quiet =
      p.quietEnabled &&
      (p.quietStart < p.quietEnd
        ? time >= p.quietStart && time < p.quietEnd
        : time >= p.quietStart || time < p.quietEnd);
    const existingKeys = new Set(
      data.notifications.map((item) => item.deliveryKey).filter(Boolean),
    );
    const delivered: Record<string, string> = JSON.parse(
      localStorage.getItem("mdm_mobile_deliveries") || "{}",
    );
    const sources = [
      ...data.tasks
        .filter((item) => !item.completed)
        .map((item) => ({
          entityType: "task",
          sourceType: "task",
          id: item.id,
          title: "Task Reminder",
          body: `${item.title}${matterName(data.matters, item.matterId) ? ` — ${matterName(data.matters, item.matterId)}` : ""}`,
          date: item.dueDate,
          time: item.reminderTime || item.googleCalendarTime || "09:00",
        })),
      ...data.events.map((item) => ({
        entityType: "calendar_event",
        sourceType: "entry",
        id: item.id,
        title: "Calendar Event",
        body: `${item.title}${matterName(data.matters, item.matterId) ? ` — ${matterName(data.matters, item.matterId)}` : ""}`,
        date: item.date,
        time: item.time || "09:00",
      })),
      ...data.importantDates
        .filter((item) => !item.completed)
        .map((item) => ({
          entityType: "important_date",
          sourceType: "important",
          id: item.id,
          title: "Important Date",
          body: `${item.title}${matterName(data.matters, item.matterId) ? ` — ${matterName(data.matters, item.matterId)}` : ""}`,
          date: item.date,
          time: item.time || "09:00",
        })),
      ...data.kivItems
        .filter((item) => item.status !== "Closed")
        .map((item) => ({
          entityType: "kiv",
          sourceType: "kiv",
          id: item.id,
          title: "KIV Review",
          body: `${item.title}${matterName(data.matters, item.matterId) ? ` — ${matterName(data.matters, item.matterId)}` : ""}`,
          date: item.reviewDate,
          time: item.reviewTime || "09:00",
        })),
    ];
    for (const source of sources) {
      const rule = data.reminderRules.find(
        (item) =>
          item.sourceType === source.sourceType && item.sourceId === source.id,
      );
      if (!rule || rule.mode === "none" || !source.date) continue;
      if (rule.snoozedUntil && new Date(rule.snoozedUntil) > now) continue;
      const due = dateAt(source.date, source.time);
      if (!due) continue;
      const start = new Date(
        due.getTime() - Number(rule.leadMinutes || 0) * 60000,
      );
      let occurrence = "";
      if (rule.mode === "once") {
        if (now < start) continue;
        occurrence = `once:${start.toISOString().slice(0, 16)}`;
      } else if (rule.mode === "daily") {
        const daily = dateAt(todayIso(), rule.dailyTime || "08:30");
        if (
          !daily ||
          now < daily ||
          now.getTime() - daily.getTime() > 2 * 3600000
        )
          continue;
        if (!rule.continueOverdue && todayIso() > source.date) continue;
        occurrence = `daily:${todayIso()}`;
      } else {
        if (now < start) continue;
        if (!rule.continueOverdue && now > due) continue;
        const interval = Math.max(15, Number(rule.repeatMinutes || 60)) * 60000;
        occurrence = `${rule.mode}:${start.toISOString().slice(0, 16)}:${Math.floor((now.getTime() - start.getTime()) / interval)}`;
      }
      const key = `${source.sourceType}:${source.id}:${occurrence}`;
      if (delivered[key] || existingKeys.has(key)) continue;
      if (quiet) continue;
      delivered[key] = now.toISOString();
      localStorage.setItem(
        "mdm_mobile_deliveries",
        JSON.stringify(
          Object.fromEntries(Object.entries(delivered).slice(-3000)),
        ),
      );
      const notification: NotificationItem = {
        id: `delivery-${btoa(key)
          .replace(/[^a-z0-9]/gi, "")
          .slice(0, 48)}`,
        type: "Reminder",
        title: source.title,
        body: source.body,
        entityType: source.entityType,
        entityId: source.id,
        page:
          source.entityType === "kiv"
            ? "kiv"
            : source.entityType === "task"
              ? "dashboard"
              : "calendar",
        read: false,
        archived: false,
        createdAt: now.toISOString(),
        updatedAt: now.toISOString(),
        deliveryKey: key,
      };
      if (rule.centre)
        await saveEntity("notification", notification, { skipActivity: true });
      if (rule.inApp && p.inAppEnabled)
        addGlassNotice({
          id: key,
          title: source.title,
          body: source.body,
          persistent: rule.mode === "persistent",
          onOpen: () => openEntity(source.entityType, source.id),
          onSnooze: () => void snoozeSource(source.entityType, source.id, 60),
          onComplete: ["task", "kiv", "important_date"].includes(
            source.entityType,
          )
            ? () => void completeSource(source.entityType, source.id)
            : undefined,
        });
      if (rule.mobilePush !== false && p.pushEnabled)
        await showSystemNotification({
          title: source.title,
          body: source.body,
          tag: key,
          url: `/?entityType=${source.entityType}&entityId=${source.id}`,
          entityType: source.entityType,
          entityId: source.id,
          persistent: rule.mode === "persistent",
        });
      if (rule.sound && p.soundEnabled) await playTabletopBell();
    }
  }, [
    addGlassNotice,
    completeSource,
    data,
    openEntity,
    saveEntity,
    session,
    snoozeSource,
  ]);
  useEffect(() => {
    if (!session) return;
    const start = window.setTimeout(() => void check(), 1800);
    const timer = window.setInterval(() => void check(), 60000);
    const visible = () => {
      if (document.visibilityState === "visible") void check();
    };
    document.addEventListener("visibilitychange", visible);
    return () => {
      window.clearTimeout(start);
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", visible);
    };
  }, [check, session]);
}

export default App;
